import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Snackbar,
  Alert,
  TextareaAutosize,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { inputHelper } from "../../Helper";
import {
  useCreateCampaignMutation,
  useGetCampaignByIdQuery,
  useUpdateCampaignMutation,
} from "../../Apis/campaignApi";
import { withAdminAuth } from "../../HOC";


export default withAdminAuth(function CampaignUpsert() {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [imageToStore, setImageToStore] = useState<any>();
  const [imageToDisplay, setImageToDisplay] = useState<string>("");

  const [campaignInputs, setCampaignInputs] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
  });

  const [loading, setLoading] = useState(false);

  const { data } = useGetCampaignByIdQuery(campaignId, { skip: !campaignId });
  const [createCampaign] = useCreateCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();

    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "info" });

    const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load for editing
  useEffect(() => {
    if (data?.result) {
      setCampaignInputs({
        name: data.result.name,
        description: data.result.description,
        startDate: data.result.startDate.split("T")[0],
        endDate: data.result.endDate.split("T")[0],
      });
      setImageToDisplay(data.result.imageUrl);
    }
  }, [data]);

  const handleCampaignInput = (e: any) => {
    setCampaignInputs(inputHelper(e, campaignInputs));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imgType = file.type.split("/")[1];
    const validImageType = ["jpeg", "jpg", "png"];

    if (file.size > 1000 * 1024) {
      setSnackbar({open: true,message:"File must be less than 1 MB", severity:"error"});
      return;
    }
    if (!validImageType.includes(imgType)) {
      setSnackbar({open: true,message:"File must be jpeg, jpg, or png", severity:"error"});
      
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    setImageToStore(file);

    reader.onload = (event) => {
      setImageToDisplay(event.target?.result as string);
    };
  };

  const handlSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!imageToStore && !campaignId) {
      setSnackbar({open: true,message:"You must upload an image", severity:"error"});
      
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("Name", campaignInputs.name);
    formData.append("Description", campaignInputs.description);
    formData.append("StartDate", campaignInputs.startDate);
    formData.append("EndDate", campaignInputs.endDate);
    if (imageToStore) formData.append("Image", imageToStore);

    let response;

    if (campaignId) {
      formData.append("Id", campaignId);
      response = await updateCampaign({ data: formData, id: campaignId });
      setSnackbar({open: true,message:"Campaign updated successfully", severity:"success"});
      
    } else {
      response = await createCampaign(formData);
      setSnackbar({open: true,message:"Campaign created successfully", severity:"success"});
    }

    setLoading(false);
    if (response) setTimeout(() => navigate("/Campaign/List"), 1000);
  };
  

  return (
    <Card elevation={4} sx={{ p: 1 }}>
      <CardHeader
        title={
          <Typography variant="h5" sx={{ color: "#eb3c96" }}>
            {!campaignId ? "Create Campaign" : "Edit Campaign"}
          </Typography>
        }
      />

      <form onSubmit={handlSubmit} encType="multipart/form-data">
        <CardContent>
          <Grid container spacing={3}>
            {/* Image Preview */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  width: "100%",
                  height: 300,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                {imageToDisplay ? (
                  <img
                    src={imageToDisplay}
                    alt="Campaign"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography>No Image</Typography>
                )}
              </Box>

              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload Campaign Image
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
            </Grid>

            {/* Form Inputs */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={3}>
                <TextField
                  label="Campaign Name"
                  name="name"
                  fullWidth
                  required
                  value={campaignInputs.name}
                  onChange={handleCampaignInput}
                />
                

                

                <TextField
                  label="Description"
                  name="description"
                  multiline
                  rows={6}
                  required
                  fullWidth
                  value={campaignInputs.description}
                  onChange={handleCampaignInput}
                /> 

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      type="date"
                      label="Start Date"
                      name="startDate"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      value={campaignInputs.startDate}
                      onChange={handleCampaignInput}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      type="date"
                      label="End Date"
                      name="endDate"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      value={campaignInputs.endDate}
                      onChange={handleCampaignInput}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* Buttons */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {campaignId
                    ? loading
                      ? "Updating..."
                      : "Update"
                    : loading
                    ? "Creating..."
                    : "Create"}
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/Campaign/List")}
                >
                  Back to List
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
});
